import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ReactEditor, RenderElementProps, useSelected, useSlateStatic } from 'slate-react';
import { FormGrid, OccupyFreeSpace, Tooltip } from '../../../primitives';
import { TextField, Typography } from '@mui/material';
import { Add, DeleteOutlined, ExpandMore, UnfoldLess } from '@mui/icons-material';
import { Editor, Transforms } from 'slate';
import { CustomElement } from '../../../../../slate';
import { EditorPlugin } from '../../slate/PowerEditorConfig';
import { useIntl } from 'react-intl';
import { usePowerEditorContext } from '../../slate/PowerEditorContext';
import { BlockWrapper, Buttons } from '../../elements/Common';
import isHotkey from 'is-hotkey';
import { generateCode } from '../common';
import { moveArrayItemTo } from '../../../../hooks/useEditArray';
import { toMap } from '../../../../api/data';
import { usePreconfiguredFields } from './usePreconfiguredFields';
import { PreconfiguredEditor } from './PreconfiguredEditor';
import { StrippedIconButton } from '../../../primitives/StrippedButtons';

export const CustomFieldsBlockElementType = "custom_fields_block";

interface Field {
    label: string;
    value: string;
}

interface WithFields {
    fieldsCodes: string[];
    fields: Record<string, Field>;
    is_collapsed?: boolean;
}

const Wrapper = styled(BlockWrapper)`
`;

const FieldWrapper = styled(FormGrid)<{ isDragged?: boolean, isDragTarget?: boolean }>`
  opacity: ${props => props.isDragged ? 0.5 : 1};
  border-top: 3px solid ${props => props.isDragTarget ? props.theme.palette.primary.main : "transparent"};
  position: relative;

  /* grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr)); */
  grid-template-columns: 1fr;
  gap: 0;

  @container (width >= 500px) {
    grid-template-columns: min(250px, 30%) 1fr; /* wide: sidebar + content */
    gap: 1rem;
  }

  & .delete-btn {
    position: absolute;
    right: 0;
    visibility: hidden;
  }

  &:hover {
    & .delete-btn {
      visibility: visible;
    }
  }
`;
FieldWrapper.defaultProps = { columns: "1fr 1fr", noMargin: true, forceEvenColumns: true };


const PreconfiguredLabel = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  justify-content: flex-start;

  border-bottom: 1px dotted #808080;
  height: 100%;
  padding-top: 1rem;
  color: ${props => props.theme.palette.text.secondary};
`;

const FieldsOuterWrapper = styled(FormGrid)`
  container-type: inline-size;
`;

export const extractCustomBlockFields = (block: RenderElementProps["element"]) => {
  const fields = Object.values((block as any).fields || {}) as { label: string; value: string }[];
  return fields.reduce<Record<string, string>>((r, { label, value }) => { r[label] = value; return r; }, {});
}


const useCustomFields = (element: Partial<WithFields> & CustomElement) => {
    const editor = useSlateStatic();
    const [insertedCode, setInsertedCode] = useState<string>("");
    
    // local state is needed to help react keep track of control state with the async updates by Slate/Transforms
    const [localState, setLocalState] = useState<WithFields>({
        fieldsCodes: element.fieldsCodes || [],
        fields: element.fields || {},
        is_collapsed: element.is_collapsed,
    });
    
    const codes = localState.fieldsCodes;
    const fields = localState.fields;

    const path = ReactEditor.findPath(editor, element);

    const updateState = (updated: WithFields) => {
        setLocalState(updated);
        Transforms.setNodes(
            editor,
            updated as any,
            { at: path },
        );
    }

    const updateCollapsed = (value: boolean) => {
      updateState({ ...localState, is_collapsed: value });
    }

    const insertField = () => {
        const newCode = generateCode();
        setInsertedCode(newCode);
        updateState({
            fieldsCodes: [ ...codes, newCode],
            fields: { ...fields, [newCode]: { label: "", value: "" }},
        })
    };

    const updateField = (code: string, changes: Partial<Field>) => {
        updateState({
            fields: { ...fields, [code]: { ...(fields[code]), ...changes }},
            fieldsCodes: localState.fieldsCodes?.includes(code) ? localState.fieldsCodes : [...localState.fieldsCodes, code],
        });
    }

    const removeField = (code: string) => {
        const filteredCodes = codes.filter(c => c !== code);
        updateState({
            fieldsCodes: filteredCodes,
            fields: filteredCodes.reduce<Record<string, Field>>((r, c) => {
                if(c !== code) {
                    r[c] = fields[c];
                } 
                return r;
            }, {}),
        });
    }

    const moveField = (idxFrom: number, idxTo: number) => {
      updateState({
        fieldsCodes: moveArrayItemTo(codes, idxFrom, idxTo),
        fields: { ...fields },
      });
    }

    return {
        codes: localState.fieldsCodes,
        fields: localState.fields,
        insertField,
        updateField,
        removeField,
        moveField,
        insertedCode,
        updateState,

        isCollapsed: !!localState.is_collapsed,
        updateCollapsed,
    }
}


export const CustomFieldsBlockElement = (props: RenderElementProps) => {
    const { element, attributes, children } = props;
    
    const {
        codes,
        fields,
        insertField,
        updateField,
        removeField,
        moveField,
        insertedCode,
        updateCollapsed,
        isCollapsed,
    } = useCustomFields(element as Partial<WithFields> & CustomElement);
    const isSelected = useSelected();

    const preconfigured = usePreconfiguredFields((element as any).preconfigured_fields);

    const { formatMessage } = useIntl();
    const { viewMode } = usePowerEditorContext();

    const [dragging,setDragging] = useState<string | null>(null);
    const [dragTarget,setDragTarget] = useState<string | null>(null);

    const fieldsValues = isCollapsed ? toMap(Object.values(fields), f => f.label, f => f.value) : {};

    const blockTitle = fieldsValues.block_title || fieldsValues.title || fieldsValues.block_type;

    return (
        <Wrapper {...attributes} isSelected={isSelected} contentEditable={false}>
            {children}

            {isCollapsed && <>
              <Buttons style={{ paddingTop: 0 }} contentEditable={false}>
                {!!blockTitle &&
                  <Typography onClick={() => updateCollapsed(false)}>{blockTitle}</Typography>}
                <OccupyFreeSpace />

                <Tooltip text_id="common.show_details">
                  <StrippedIconButton onClick={() => updateCollapsed(false)}><ExpandMore /></StrippedIconButton>
                </Tooltip>
              </Buttons>
            </>}

            {!isCollapsed && <>
              <FieldsOuterWrapper columns="1fr" gap="dense" contentEditable={false}>
                {!!preconfigured.fields.length && preconfigured.fields.map(f => (
                  <FieldWrapper key={f.code}>
                    <PreconfiguredLabel>
                      <Typography>{f.label} </Typography>
                      <Typography variant="caption" color="textSecondary">({f.code})</Typography>
                    </PreconfiguredLabel>

                    <PreconfiguredEditor
                      field={f}
                      schema={preconfigured.schema}
                      fields={fields}
                      updateField={updateField}
                      />
                  </FieldWrapper>
                ))}
                {codes.filter(c => !preconfigured.fieldsCodes.has(c)).map((f,idx) => (
                    <FieldWrapper
                      key={f}
                      draggable={!viewMode}
                      isDragged={f === dragging}
                      isDragTarget={f === dragTarget}
                      onDragStart={() => setDragging(f)}
                      onDragEnter={e => { e.preventDefault(); setDragTarget(f)}}
                      onDragOver={e => { e.preventDefault(); setDragTarget(f)}}
                      onDragLeave={e => { e.preventDefault(); setDragTarget(null)}}
                      onDrop={e => {
                        e.stopPropagation();
                        if(dragging && f !== dragging) {
                          const idxFrom = codes.indexOf(dragging);
                          if(idxFrom >= 0 && idxFrom !== idx) {
                            moveField(idxFrom, idx);
                          }
                        }
                        setDragging(null);
                        setDragTarget(null);
                      }}>
                        <TextField
                            key="label"
                            value={fields[f]?.label || ""}
                            onChange={e => updateField(f, { label: e.target.value })}
                            label=""
                            placeholder={formatMessage({ id: "powerdoc.plugins.custom_fields_block.field.label"})}
                            autoFocus={f === insertedCode}
                            InputProps={{ readOnly: viewMode }}
                            />
                        <TextField
                            key="value"
                            value={fields[f]?.value || ""}
                            onChange={e => updateField(f, { value: e.target.value })}
                            label=""
                            placeholder={formatMessage({ id: "powerdoc.plugins.custom_fields_block.field.value"})}
                            multiline
                            onKeyDown={e => {
                                if(isHotkey("mod+Enter", e)) {
                                    insertField();
                                    e.preventDefault();
                                }
                            }}
                            InputProps={{ readOnly: viewMode }}
                            />
                        <StrippedIconButton className="delete-btn" onClick={() => removeField(f)}><DeleteOutlined /></StrippedIconButton>
                    </FieldWrapper>
                ))}
              </FieldsOuterWrapper>
              <Buttons contentEditable={false}>
                <StrippedIconButton onClick={() => insertField()}><Add /></StrippedIconButton>
                <StrippedIconButton onClick={() => updateCollapsed(true)}><UnfoldLess /></StrippedIconButton>
              </Buttons>
            </>}


        </Wrapper>
    );
}

const withCustomFieldsBlock = (editor: Editor) => {
    const { isVoid } = editor;
    editor.isVoid = (element: CustomElement) => {
        return element.type === CustomFieldsBlockElementType ? true : isVoid(element);
    }
    
    return editor;
}

export const CustomFieldsBlockPlugin: EditorPlugin = {
    key: "custom-fields-block",
    commands: [{
        name: "insert-custom-fields-block",
        menu: { section: "insert-item", label: "Custom fields", label_id: "powerdoc.plugins.custom_fields_block.title" },
        invoke: editor => {
            const firstFieldCode = generateCode();
            editor.insertNode({
                type: CustomFieldsBlockElementType,
                fieldsCodes: [firstFieldCode],
                fields: { [firstFieldCode]: { label: "", value: "" }},
                children: [{ text: "", }],
            } as CustomElement);
        }
    }],
    customBlocks: { [CustomFieldsBlockElementType]: CustomFieldsBlockElement },
    inject: e => withCustomFieldsBlock(e),
};
